import * as React from "react";
import { RouteComponentProps } from "react-router";
import { ChildProps, gql, graphql } from "react-apollo";
import { formatCurrencyWithRands } from "../../../../utils/filters";
import WithholdWithdrawalButton from "../../Withdrawals/WithholdWithdrawalButton";
import PauseWithdrawalButton from "../../Withdrawals/PauseWithdrawalButton";
import ResumeWithdrawalButton from "../../Withdrawals/ResumeWithdrawalButton";

type GqlActivityLog = {
  created_at: string;
  comment: string;
  username: string;
  event: string;
  snapmin_url: string;
};

type GqlWithdrawalRequest = {
  created_at: string;
  bank_account_number: string;
  bank_branch_code: string;
  bank_name: string;
  bank_recipient_name: string;
  fee_amount: string;
  manual_verification: string;
  payable_amount: string;
  reference: string;
  snapmin_url: string;
  statement_reference: string;
  status: string;
  user: {
    email: string;
  };
  withdraw_amount;
  withdrawal_batch: {
    id: string;
    status: string;
  };
  activity_logs: Array<GqlActivityLog>;
};

type GqlData = {
  withdrawal_requests: Array<GqlWithdrawalRequest>;
};

type Props = ChildProps<
  BOComponentProps & RouteComponentProps<{ id: string }>,
  GqlData
>;

type State = {
  createdAtFrom: string;
  createdAtTo: string;
};

class WithdrawalRequestSummary extends React.Component<Props, State> {
  state = {
    createdAtFrom: null,
    createdAtTo: null,
  };

  componentDidMount() {
    this.props.setHeader({
      title: "Wallet Withdrawal Requests",
      description: "SnapScan Wallet Withdrawal Requests",
    });
  }

  onAction() {
    this.props.data.refetch();
  }

  render() {
    const summary = this.props.data.withdrawal_requests?.length
      ? this.props.data.withdrawal_requests[0]
      : null;

    if (this.props.data.loading || !summary) {
      return "";
    }

    return (
      <section id="Wallet_Withdrawal_Request_Summary" className="padded">
        <div
          style={{
            display: "flex",
            flexDirection: "row",
          }}
        >
          <div
            style={{
              flex: "auto",
              marginRight: "1em",
            }}
          >
            {Template.title(summary?.user?.email)}
          </div>

          {
            <div
              style={{
                alignSelf: "center",
                flex: "inherit",
                display: "flex",
              }}
            >
              {summary.status === "reserved" ||
              summary.withdrawal_batch?.status === "pending" ? (
                <WithholdWithdrawalButton
                  withdrawalReference={summary.reference}
                  onAction={this.onAction.bind(this)}
                />
              ) : null}
              {(summary.status === "reserved" ||
                summary.status === "released") &&
              summary.withdrawal_batch?.status !== "submitted" ? (
                <PauseWithdrawalButton
                  withdrawalReference={summary.reference}
                  onAction={this.onAction.bind(this)}
                />
              ) : null}
              {summary.status === "paused" ? (
                <ResumeWithdrawalButton
                  withdrawalReference={summary.reference}
                  onAction={this.onAction.bind(this)}
                />
              ) : null}
            </div>
          }
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            height: "calc(100% - 35px)",
          }}
        >
          <aside
            style={{
              maxWidth: "220px",
              padding: "5px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {Template.activityLogs(summary.activity_logs)}
          </aside>

          <aside
            style={{
              width: "calc(100% - 220px)",
              padding: "5px",
              overflow: "auto",
            }}
          >
            {Template.generalSummary(summary)}
            {Template.amounts(summary)}
            {Template.bankingDetails(summary)}
          </aside>
        </div>
      </section>
    );
  }
}

const withdrawalSummaryGql = gql`
  query WalletWithdrawalSummary($reference: ID) {
    withdrawal_requests(reference: $reference) {
      created_at
      bank_account_number
      bank_branch_code
      bank_name
      bank_recipient_name
      fee_amount
      manual_verification
      payable_amount
      reference
      snapmin_url
      statement_reference
      status
      user {
        email
      }
      withdraw_amount
      withdrawal_batch {
        id
        status
      }
      activity_logs {
        created_at
        comment
        username
        event
        snapmin_url
      }
    }
  }
`;

const withWithdrawalSummaryGqlData = graphql<GqlData, Props>(
  withdrawalSummaryGql,
  {
    options(props) {
      return {
        variables: {
          reference: props.match.params.id,
        },
      };
    },
  }
);

export default withWithdrawalSummaryGqlData(WithdrawalRequestSummary);

class Template {
  static title(email: string) {
    return (
      <h3
        style={{
          borderBottom: "1px solid rgb(204, 204, 204)",
          fontWeight: 400,
          marginTop: 0,
        }}
      >
        {`Wallet Withdrawal - ${email}`}
      </h3>
    );
  }

  static generalSummary(summary: GqlWithdrawalRequest) {
    return (
      <div>
        <h3
          style={{
            borderBottom: "1px solid rgb(204, 204, 204)",
            paddingBottom: "10px",
            fontWeight: 400,
            marginBottom: "12px",
            marginTop: "12px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <span style={{ flex: 1 }}>General details</span>
        </h3>
        <table>
          <tbody>
            <tr style={{ fontSize: "14px" }}>
              <td style={{ padding: "5px", paddingLeft: 0 }}>
                <b style={{ fontWeight: 400 }}>Date:</b>
              </td>
              <td style={{ padding: "5px" }}>{summary.created_at}</td>
              <td style={{ padding: "5px", paddingLeft: "20px" }}>
                <b style={{ fontWeight: 400 }}>Status:</b>
              </td>
              <td style={{ padding: "5px" }}>{summary.status}</td>
            </tr>
            <tr style={{ fontSize: "14px" }}>
              <td style={{ padding: "5px", paddingLeft: 0 }}>
                <b style={{ fontWeight: 400 }}>Payable amount:</b>
              </td>
              <td style={{ padding: "5px" }}>
                {formatCurrencyWithRands(summary.payable_amount)}
              </td>
              <td style={{ padding: "5px", paddingLeft: "20px" }}>
                <b style={{ fontWeight: 400 }}>Fee amount:</b>
              </td>
              <td style={{ padding: "5px" }}>
                {formatCurrencyWithRands(summary.fee_amount)}
              </td>
            </tr>
            <tr style={{ fontSize: "14px" }}>
              <td style={{ padding: "5px", paddingLeft: 0 }}>
                <b style={{ fontWeight: 400 }}>Withdrawal amount:</b>
              </td>
              <td style={{ padding: "5px" }}>
                {formatCurrencyWithRands(summary.withdraw_amount)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  static amounts(summary: GqlWithdrawalRequest) {
    return (
      <div>
        <h3
          style={{
            borderBottom: "1px solid rgb(204, 204, 204)",
            paddingBottom: "10px",
            fontWeight: 400,
            marginBottom: "12px",
            marginTop: "12px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <span style={{ flex: 1 }}>Amounts</span>
        </h3>
        <table>
          <tbody>
            <tr style={{ fontSize: "14px" }}>
              <td style={{ padding: "5px", paddingLeft: 0 }}>
                <b style={{ fontWeight: 400 }}>Payable amount:</b>
              </td>
              <td style={{ padding: "5px" }}>
                {formatCurrencyWithRands(summary.payable_amount)}
              </td>
            </tr>
            <tr style={{ fontSize: "14px" }}>
              <td style={{ padding: "5px", paddingLeft: 0 }}>
                <b style={{ fontWeight: 400 }}>Fee amount:</b>
              </td>
              <td style={{ padding: "5px" }}>
                {formatCurrencyWithRands(summary.fee_amount)}
              </td>
            </tr>
            <tr style={{ fontSize: "14px" }}>
              <td style={{ padding: "5px", paddingLeft: 0 }}>
                <b style={{ fontWeight: 400 }}>Withdrawal amount:</b>
              </td>
              <td style={{ padding: "5px" }}>
                {formatCurrencyWithRands(summary.withdraw_amount)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  static bankingDetails(summary: GqlWithdrawalRequest) {
    return (
      <div>
        <h3
          style={{
            borderBottom: "1px solid rgb(204, 204, 204)",
            paddingBottom: "10px",
            fontWeight: 400,
            marginBottom: "12px",
            marginTop: "12px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <span style={{ flex: 1 }}>Banking details</span>
        </h3>
        <table>
          <tbody>
            <tr style={{ fontSize: "14px" }}>
              <td style={{ padding: "5px", paddingLeft: 0 }}>
                <b style={{ fontWeight: 400 }}>Bank name:</b>
              </td>
              <td style={{ padding: "5px" }}>{summary.bank_name}</td>
              <td style={{ padding: "5px", paddingLeft: "20px" }}>
                <b style={{ fontWeight: 400 }}>Bank branch code:</b>
              </td>
              <td style={{ padding: "5px" }}>{summary.bank_branch_code}</td>
            </tr>
            <tr style={{ fontSize: "14px" }}>
              <td style={{ padding: "5px", paddingLeft: 0 }}>
                <b style={{ fontWeight: 400 }}>Bank account number:</b>
              </td>
              <td style={{ padding: "5px" }}>{summary.bank_account_number}</td>
              <td style={{ padding: "5px", paddingLeft: "20px" }}>
                <b style={{ fontWeight: 400 }}>Bank recipient name:</b>
              </td>
              <td style={{ padding: "5px" }}>{summary.bank_recipient_name} </td>
            </tr>
            <tr style={{ fontSize: "14px" }}>
              <td style={{ padding: "5px", paddingLeft: 0 }}>
                <b style={{ fontWeight: 400 }}>Statement reference:</b>
              </td>
              <td style={{ padding: "5px" }}>{summary.statement_reference}</td>
              <td style={{ padding: "5px", paddingLeft: "20px" }}>
                <b style={{ fontWeight: 400 }}>Manual verification:</b>
              </td>
              <td style={{ padding: "5px" }}>{summary.manual_verification}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  static activityLogs(logs: Array<GqlActivityLog>) {
    return (
      <section>
        <h4
          style={{
            margin: 0,
            border: "1px solid #999",
            borderBottom: 0,
            padding: "5px",
            fontSize: "13px",
            fontWeight: 400,
          }}
        >
          Activity Log
        </h4>
        <ul
          style={{
            border: "1px solid #999",
            margin: "10px 0",
            marginTop: "0",
            padding: "5px 0",
            paddingLeft: "25px",
            flex: 1,
            overflow: "auto",
          }}
        >
          {logs.length ? (
            logs.map((val, key) => {
              return (
                <li
                  key={key}
                  style={{
                    padding: "5px 0",
                    fontSize: "10pt",
                  }}
                >
                  <i>{val.username}</i> - {val.comment}
                </li>
              );
            })
          ) : (
            <li
              style={{
                padding: "5px 0",
                listStyle: "none",
              }}
            >
              No activity on withdrawal.
            </li>
          )}
        </ul>
      </section>
    );
  }
}
